/**
 * BetaLanding — the signed-out front door while the launch gate is on
 * (/coming-soon, site_config/launch stage 'beta' or 'closed'), in Floodlight.
 * Classic keeps the original coming-soon page (MainPage.js).
 *
 * The idea: a floodlight. A beam sweeps a board of sportsbook prices and
 * settles on the best number, which is what the product does. The board is a
 * labelled example (odds are on a sandbox feed until live pricing), its EV is
 * computed from its own numbers, and every claim on the page is a shipped
 * feature. No invented stats, users or testimonials.
 *
 * Calls only the public joinWaitlist callable (Day 1 gate: signed-out pages
 * never call gated functions). Motion is CSS (compositor only) and stops
 * under reduced motion, where every element shows its final state.
 */
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable, Image, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import useWaitlist from '../hooks/useWaitlist';
import Reveal from '../components/motion/Reveal';
import LegalFooter from '../components/LegalFooter';
import { FL_GROUND } from '../components/floodlight/FLPage';
import { FLButton, FLHeadline, FLMarquee, FLKicker, FL, FONT, label, body, nums, gutter, glass, ensureFloodlight } from '../components/floodlight';
import { americanToImplied, americanToDecimal } from '../utils/betMath';
import { IS_WEB } from '../utils/motion';
import injectCss from '../utils/injectCss';

const LOGO = require('../assets/logo-mark-160.png');

// The example board. Fixed, clearly labelled numbers: never live odds.
export const EXAMPLE = {
    query: 'chiefs -3.5',
    market: 'Kansas City Chiefs −3.5 · Spread',
    fair: -106, // assumed fair price for the example (the board shows one side only)
    rows: [
        ['DraftKings', -110],
        ['FanDuel', -108],
        ['BetMGM', -112],
        ['Caesars', -110],
        ['Fanatics', -104],
        ['BetRivers', -115],
    ],
};

/** Best row (highest payout) and its EV against the example's fair price. */
export function exampleMath(ex = EXAMPLE) {
    let best = 0;
    ex.rows.forEach(([, p], i) => { if (americanToDecimal(p) > americanToDecimal(ex.rows[best][1])) best = i; });
    const fairProb = americanToImplied(ex.fair);
    const ev = fairProb * americanToDecimal(ex.rows[best][1]) - 1;
    return { best, ev, fairProb };
}

const fmt = (p) => (p > 0 ? `+${p}` : `−${Math.abs(p)}`);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Recognizable names from utils/sportsbooks.json (the same list /why shows).
const BOOKS = ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'Fanatics', 'BetRivers', 'Hard Rock Bet', 'Pinnacle', 'Circa Sports', 'Bovada', 'BetOnline', 'Novig', 'Kalshi', 'Polymarket', 'PrizePicks', 'Underdog Fantasy', 'Fliff', 'Betfair'];

const STEPS = [
    ['Ask it plainly', 'Type the bet the way you would say it: "mahomes passing yards", "celtics -4.5", "best nba props tonight".'],
    ['See every book', 'Every price for that line side by side, with the vig stripped out against the whole market to estimate fair odds.'],
    ['Take the best number', 'Ranked by expected value, with the book that pays the most. Then bet it wherever you already play.'],
];

const FEATURES = [
    ['search', 'Natural-language search', 'Players, teams, markets and lines in one box, across sports and leagues.'],
    ['scale-outline', 'Fair odds on every price', 'Devigged against the full market, not one sportsbook’s opinion.'],
    ['git-merge-outline', 'Same-game parlay optimizer', 'Stack legs from one game and price the combination at the major books.'],
    ['pulse-outline', 'Game and injury tickers', 'Upcoming games and the latest injury news across the top of every page.'],
];

const PROMISES = [
    'Free for the whole beta. No card.',
    'Invites go out in small batches.',
    'Feedback goes straight to the people building it.',
];

const ROW_H = 46;

const BEST = exampleMath(EXAMPLE).best;
const N = EXAMPLE.rows.length;
const rowDelays = EXAMPLE.rows.map((_, i) => `[data-bl-row]:nth-child(${i + 2}) { animation-delay: ${1600 + i * 70}ms; }`).join('\n  ');

// Values are generated from EXAMPLE (react-native-web drops CSS custom properties in style objects).
const CSS = `
[data-bl-sweep] { position: absolute; top: -10%; width: 38%; height: 120%; transform-origin: 50% 0; will-change: transform;
  background-image: linear-gradient(180deg, rgba(196,161,255,0.10) 0%, rgba(124,58,237,0.04) 38%, transparent 72%);
  clip-path: polygon(46% 0, 54% 0, 100% 100%, 0 100%); filter: blur(18px); }
[data-bl-sweep="a"] { left: -6%; transform: rotate(-22deg); }
[data-bl-sweep="b"] { right: -6%; transform: rotate(20deg); }
[data-bl-beam] { transform: translateY(${BEST * ROW_H}px); box-shadow: 0 0 40px rgba(74,222,128,0.18); }
[data-bl-board] { box-shadow: 0 40px 120px -40px rgba(124,58,237,0.45); }
@media (prefers-reduced-motion: no-preference) {
  [data-bl-sweep="a"] { animation: bl-sweep-a 14s cubic-bezier(.45,0,.55,1) infinite alternate; }
  [data-bl-sweep="b"] { animation: bl-sweep-b 18s cubic-bezier(.45,0,.55,1) infinite alternate; }
  @keyframes bl-sweep-a { from { transform: rotate(-28deg); } to { transform: rotate(-16deg); } }
  @keyframes bl-sweep-b { from { transform: rotate(14deg); } to { transform: rotate(26deg); } }
  [data-bl-type] { animation: bl-type 1.1s steps(${EXAMPLE.query.length}, end) .5s both; }
  @keyframes bl-type { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
  [data-bl-caret] { animation: bl-blink 1s steps(1) infinite; }
  @keyframes bl-blink { 50% { opacity: 0; } }
  [data-bl-row] { animation: bl-row .5s cubic-bezier(.16,1,.3,1) both; }
  ${rowDelays}
  @keyframes bl-row { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  [data-bl-beam] { animation: bl-beam 1.6s cubic-bezier(.65,0,.35,1) 2.2s both; }
  @keyframes bl-beam { 0% { opacity: 0; transform: translateY(0); } 15% { opacity: 1; transform: translateY(0); } 55% { transform: translateY(${(N - 1) * ROW_H}px); } 100% { opacity: 1; transform: translateY(${BEST * ROW_H}px); } }
  [data-bl-row="best"] { animation-name: bl-row, bl-lit; animation-duration: .5s, .6s; animation-delay: ${1600 + BEST * 70}ms, 3.6s; animation-fill-mode: both, both; }
  @keyframes bl-lit { from { background-color: transparent; border-color: transparent; } }
  [data-bl-tag] { animation: bl-tag .4s cubic-bezier(.34,1.4,.64,1) 3.7s both; }
  @keyframes bl-tag { from { opacity: 0; transform: scale(.6); } to { opacity: 1; transform: none; } }
  [data-bl-stats] { animation: bl-row .6s cubic-bezier(.16,1,.3,1) 3.9s both; }
}
[data-bl-row]:hover { background-color: rgba(255,255,255,0.04); }
[data-bl-input]:focus-within { border-color: #A78BFA !important; box-shadow: 0 0 0 3px rgba(167,139,250,.28); }
[data-bl-link]:hover [data-bl-link-text] { color: #fff; text-decoration: underline; text-underline-offset: 4px; }
[data-bl-link]:focus-visible { outline: 2px solid #A78BFA; outline-offset: 4px; border-radius: 6px; }
`;

export default function BetaLanding() {
    if (IS_WEB) { ensureFloodlight(); injectCss('bo-beta-landing', CSS); }
    const { navigateTo, launch } = useNavigation();
    const { width } = useWindowDimensions();
    const pad = gutter(width);
    const wide = width >= 1024;
    const waitlist = useWaitlist();
    const waitlistOpen = launch?.waitlistEnabled !== false;
    // "Best number." is the longest line (12 caps at width 104); fit it to its column.
    const heroCol = wide ? (Math.min(width, 1280) - pad * 2 - 64) * 0.54 : width - pad * 2;
    const heroSize = Math.max(36, Math.min(92, Math.floor(heroCol / 8.4)));

    return (
        <View style={styles.root}>
            {IS_WEB && <View pointerEvents="none" aria-hidden style={[StyleSheet.absoluteFill, { backgroundImage: FL_GROUND }]} />}
            {IS_WEB && <Beams />}
            <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                {/* Top bar */}
                <View style={[styles.bar, { paddingHorizontal: pad }]} accessibilityRole="banner">
                    <View style={styles.brand}>
                        <Image source={LOGO} style={styles.logo} accessibilityLabel="BetOptimized" />
                        <Text style={styles.brandText}>BetOptimized</Text>
                        <View style={styles.betaPill}><Text style={[label(10), styles.betaPillText]}>Private beta</Text></View>
                    </View>
                    <TextLink label="Sign in" onPress={() => navigateTo('Login')} />
                </View>

                {/* Hero */}
                <View style={[styles.hero, { paddingHorizontal: pad }, wide && styles.heroWide]}>
                    <View style={wide ? { flex: 1.08, paddingRight: 64 } : null}>
                        <FLKicker>Invite-only beta · Free</FLKicker>
                        <FLHeadline lines={['Every book.', 'One search.', 'Best number.']} accentLine={2} size={heroSize} style={{ marginTop: 22 }} />
                        <Text style={[body(wide ? 19 : 17), styles.lede]}>
                            Type a bet the way you would say it. BetOptimized lines up the price at every sportsbook we track, strips out the vig to estimate fair odds, and puts the light on the number worth taking.
                        </Text>
                        {waitlistOpen
                            ? <AccessForm waitlist={waitlist} id="hero" />
                            : <Text style={[body(16), { marginTop: 28 }]}>The waitlist is closed for now. If you have an invite, sign in.</Text>}
                        <View style={styles.signInRow}>
                            <Text style={body(15)}>Already invited? </Text>
                            <TextLink label="Sign in" onPress={() => navigateTo('Login')} arrow />
                        </View>
                    </View>
                    <View style={wide ? { flex: 0.92 } : { marginTop: 48 }}>
                        <PriceBoard />
                    </View>
                </View>

                {/* Books band */}
                <View style={{ marginTop: wide ? 96 : 64 }}>
                    <FLMarquee items={BOOKS} size={wide ? 40 : 24} duration={60} />
                    <Text style={[body(14), styles.bandNote, { paddingHorizontal: pad }]}>
                        Priced side by side: major US books, sharp offshore books, exchanges, prediction markets and DFS apps, including DraftKings, FanDuel, BetMGM, Caesars and Pinnacle.
                    </Text>
                </View>

                {/* How it works */}
                <View style={[styles.section, { paddingHorizontal: pad }]}>
                    <Reveal>
                        <FLKicker index={1}>How it works</FLKicker>
                        <FLHeadline lines={['Three steps', 'to the number']} size={wide ? 52 : 32} level={2} enter={false} style={{ marginTop: 18 }} />
                    </Reveal>
                    <View style={[styles.grid, wide && styles.gridWide]}>
                        {STEPS.map(([t, b], i) => (
                            <Reveal key={t} index={i} style={[styles.step, wide && { flex: 1 }]}>
                                <Text style={[styles.stepNum, nums]} aria-hidden>{String(i + 1).padStart(2, '0')}</Text>
                                <Text style={styles.cardTitle} accessibilityRole="header" aria-level={3}>{t}</Text>
                                <Text style={body(15)}>{b}</Text>
                            </Reveal>
                        ))}
                    </View>
                </View>

                {/* In the beta */}
                <View style={[styles.section, { paddingHorizontal: pad }]}>
                    <Reveal>
                        <FLKicker index={2}>In the beta</FLKicker>
                        <FLHeadline lines={['What you get', 'on day one']} size={wide ? 52 : 32} level={2} enter={false} style={{ marginTop: 18 }} />
                    </Reveal>
                    <View style={[styles.grid, wide && styles.gridWide, { flexWrap: 'wrap' }]}>
                        {FEATURES.map(([icon, t, b], i) => (
                            <Reveal key={t} index={i} style={[styles.feature, wide && styles.featureWide]}>
                                <View style={styles.featureIcon}><Ionicons name={icon} size={20} color="#C4A1FF" /></View>
                                <View style={{ flex: 1 }}>
                                    <Text style={styles.cardTitle} accessibilityRole="header" aria-level={3}>{t}</Text>
                                    <Text style={body(15)}>{b}</Text>
                                </View>
                            </Reveal>
                        ))}
                    </View>
                </View>

                {/* Why invite-only + closing CTA */}
                <View style={[styles.section, { paddingHorizontal: pad }]}>
                    <Reveal style={[styles.closer, wide && styles.closerWide]}>
                        <View style={wide ? { flex: 1, paddingRight: 56 } : null}>
                            <FLKicker index={3}>Why invite-only</FLKicker>
                            <FLHeadline lines={['Small groups.', 'Real feedback.']} size={wide ? 48 : 30} level={2} enter={false} style={{ marginTop: 18 }} />
                            <Text style={[body(16), { marginTop: 18, maxWidth: 520 }]}>
                                We are letting people in a few at a time so we can watch every search, fix what breaks and tune the math with real bettors before the doors open.
                            </Text>
                            <View style={{ marginTop: 22, gap: 12 }}>
                                {PROMISES.map((p) => (
                                    <View key={p} style={styles.promise}>
                                        <Ionicons name="checkmark" size={16} color="#4ADE80" />
                                        <Text style={[body(15), { color: FL.ink }]}>{p}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                        <View style={wide ? { flex: 1 } : { marginTop: 36 }}>
                            <Text style={[label(12), { color: FL.ink2, letterSpacing: 2 }]}>Request an invite</Text>
                            {waitlistOpen
                                ? <AccessForm waitlist={waitlist} id="closer" compact />
                                : <Text style={[body(16), { marginTop: 16 }]}>The waitlist is closed for now.</Text>}
                        </View>
                    </Reveal>
                </View>

                {/* Responsible gambling + legal */}
                <View style={[styles.footer, { paddingHorizontal: pad }]} accessibilityRole="contentinfo">
                    <View style={styles.rg}>
                        <View style={styles.badge21}><Text style={styles.badge21Text}>21+</Text></View>
                        <Text style={[body(13), { flex: 1, color: FL.muted }]}>
                            For adults 21 and over where sports betting is legal. BetOptimized is an odds research tool, not a sportsbook: we never take bets or hold funds. Gambling problem? Call 1-800-GAMBLER.
                        </Text>
                    </View>
                    <LegalFooter inline />
                </View>
            </ScrollView>
        </View>
    );
}

/** Email → joinWaitlist (public). One hook instance feeds both forms, so a join shows done in both. */
function AccessForm({ waitlist, id, compact = false }) {
    const { email, setEmail, sending, sent, onNotify } = waitlist;
    const narrow = useWindowDimensions().width < 520;
    const [error, setError] = useState('');
    const [joined, setJoined] = useState('');
    const submit = async () => {
        const v = email.trim();
        if (!EMAIL_RE.test(v)) { setError('Enter a valid email address.'); return; }
        setError('');
        setJoined(v);
        await onNotify();
    };

    const errId = `bl-err-${id}`;
    // One live region stays mounted, so the success message is announced when it swaps in.
    return <View aria-live="polite">{sent ? (
        <View style={[styles.done, compact && { marginTop: 16 }]} testID={`bl-done-${id}`}>
            <View style={styles.doneIcon}><Ionicons name="checkmark" size={18} color="#0B0B0B" /></View>
            <View style={{ flex: 1 }}>
                <Text style={styles.doneTitle}>You’re on the list.</Text>
                <Text style={body(14)}>We’ll email {joined || 'you'} when your spot opens. Check spam if it doesn’t show up.</Text>
            </View>
        </View>
    ) : (
        <View style={[styles.form, compact && { marginTop: 16 }]}>
            <View style={styles.formRow}>
                <View style={styles.inputWrap} dataSet={{ blInput: '' }}>
                    <Ionicons name="mail-outline" size={18} color="rgba(255,255,255,0.56)" />
                    <TextInput
                        value={email}
                        onChangeText={(t) => { setEmail(t); if (error) setError(''); }}
                        onSubmitEditing={submit}
                        placeholder="you@email.com"
                        placeholderTextColor="rgba(255,255,255,0.4)"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        inputMode="email"
                        accessibilityLabel="Email address"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errId : undefined}
                        style={styles.input}
                        testID={`bl-email-${id}`}
                    />
                </View>
                <FLButton label="Request access" variant="green" size="lg" busy={sending} busyLabel="Sending" onPress={submit} testID={`bl-submit-${id}`} style={narrow ? { flexGrow: 1 } : null} />
            </View>
            {error
                ? <Text nativeID={errId} style={[body(14), { color: '#FF8A7E', marginTop: 10 }]} role="alert">{error}</Text>
                : <Text style={[body(14), { color: FL.muted, marginTop: 10 }]}>Free for the whole beta. We’ll email you when your spot opens.</Text>}
        </View>
    )}</View>;
}

function TextLink({ label: text, onPress, arrow = false }) {
    return (
        <Pressable onPress={onPress} accessibilityRole="link" dataSet={{ blLink: '' }} hitSlop={8} style={styles.link}>
            <Text style={styles.linkText} dataSet={{ blLinkText: '' }}>{text}{arrow ? ' →' : ''}</Text>
        </Pressable>
    );
}

/** Two soft stadium beams from the top corners (decorative; still under reduced motion). */
function Beams() {
    return (
        <View pointerEvents="none" aria-hidden style={StyleSheet.absoluteFill}>
            <View dataSet={{ blSweep: 'a' }} />
            <View dataSet={{ blSweep: 'b' }} />
        </View>
    );
}

/** The example board: a typed search, six books, a beam that settles on the best price. */
function PriceBoard() {
    const { best, ev, fairProb } = useMemo(() => exampleMath(EXAMPLE), []);
    const bestRow = EXAMPLE.rows[best];
    return (
        <View style={styles.board} testID="bl-board" dataSet={{ blBoard: '' }}>
            <View style={styles.boardHead}>
                <Text style={[label(11), { color: FL.ink2 }]}>Example search</Text>
                <View style={styles.exampleTag}><Text style={[label(10), { color: '#F0B43C' }]}>Illustrative · not live odds</Text></View>
            </View>
            <View style={styles.searchBar} aria-label={`Search: ${EXAMPLE.query}`}>
                <Ionicons name="search" size={16} color="rgba(255,255,255,0.56)" />
                <Text style={styles.searchText} dataSet={{ blType: '' }}>{EXAMPLE.query}</Text>
                <View style={styles.caret} dataSet={{ blCaret: '' }} />
            </View>
            <Text style={[label(11), { marginTop: 18, marginBottom: 8 }]}>{EXAMPLE.market}</Text>
            <View style={{ position: 'relative' }} accessibilityRole="list">
                {IS_WEB && <View dataSet={{ blBeam: '' }} style={styles.beamRow} aria-hidden />}
                {EXAMPLE.rows.map(([book, price], i) => {
                    const isBest = i === best;
                    return (
                        <View
                            key={book}
                            accessibilityRole="listitem"
                            dataSet={{ blRow: isBest ? 'best' : '' }}
                            style={[styles.row, isBest && styles.rowBest]}
                        >
                            <Text style={[styles.book, isBest && { color: '#fff' }]}>{book}</Text>
                            {isBest && <View style={styles.bestTag} dataSet={{ blTag: '' }}><Text style={styles.bestTagText}>Best</Text></View>}
                            <Text style={[styles.implied, nums]}>{(americanToImplied(price) * 100).toFixed(1)}%</Text>
                            <Text style={[styles.price, nums, isBest && { color: '#4ADE80' }]}>{fmt(price)}</Text>
                        </View>
                    );
                })}
            </View>
            <View style={styles.stats} dataSet={{ blStats: '' }}>
                <Stat k="Fair odds" v={fmt(EXAMPLE.fair)} sub={`${(fairProb * 100).toFixed(1)}% no-vig`} />
                <Stat k="Best price" v={fmt(bestRow[1])} sub={bestRow[0]} />
                <Stat k="Expected value" v={`${ev >= 0 ? '+' : '−'}${Math.abs(ev * 100).toFixed(1)}%`} sub="per bet, long run" good={ev > 0} />
            </View>
        </View>
    );
}

function Stat({ k, v, sub, good }) {
    return (
        <View style={styles.stat}>
            <Text style={label(10)}>{k}</Text>
            <Text style={[styles.statV, nums, good && { color: '#4ADE80' }]}>{v}</Text>
            <Text style={[body(12), { color: FL.muted }]} numberOfLines={1}>{sub}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    root: { flex: 1, backgroundColor: '#000', overflow: 'hidden' },
    scroll: { flex: 1 },
    scrollContent: { width: '100%', maxWidth: 1280, alignSelf: 'center', paddingBottom: 24 },
    bar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 22, paddingBottom: 10 },
    brand: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    logo: { width: 30, height: 30, borderRadius: 8 },
    brandText: { fontFamily: FONT, fontSize: 17, fontWeight: '700', color: FL.ink, letterSpacing: -0.2 },
    betaPill: { marginLeft: 4, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(196,161,255,0.35)', backgroundColor: 'rgba(124,58,237,0.14)' },
    betaPillText: { color: '#C4A1FF', letterSpacing: 1.4 },
    link: { paddingVertical: 6 },
    linkText: { fontFamily: FONT, fontSize: 15, fontWeight: '600', color: FL.ink2 },
    hero: { paddingTop: 48 },
    heroWide: { flexDirection: 'row', alignItems: 'center', paddingTop: 72, minHeight: 640 },
    lede: { marginTop: 24, maxWidth: 560 },
    form: { marginTop: 32, maxWidth: 560 },
    formRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
    inputWrap: {
        flexGrow: 1, flexBasis: 240, minHeight: 56, flexDirection: 'row', alignItems: 'center', gap: 10,
        paddingHorizontal: 16, ...glass({ radius: 999 }), backgroundColor: 'rgba(255,255,255,0.05)',
    },
    input: { flex: 1, minWidth: 0, height: 54, fontFamily: FONT, fontSize: 16, color: '#fff', outlineStyle: 'none' },
    signInRow: { flexDirection: 'row', alignItems: 'center', marginTop: 20 },
    done: { marginTop: 32, maxWidth: 560, flexDirection: 'row', gap: 14, alignItems: 'flex-start', padding: 18, ...glass({ radius: 18 }), borderColor: 'rgba(74,222,128,0.35)' },
    doneIcon: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#4ADE80', alignItems: 'center', justifyContent: 'center' },
    doneTitle: { fontFamily: FONT, fontSize: 17, fontWeight: '700', color: '#fff', marginBottom: 4 },
    board: { ...glass({ strong: true, radius: 24 }), padding: 20 },
    boardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
    exampleTag: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: 'rgba(240,180,60,0.12)' },
    searchBar: { marginTop: 14, height: 48, borderRadius: 14, paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: 'rgba(0,0,0,0.35)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
    searchText: { fontFamily: FONT, fontSize: 16, color: '#fff' },
    caret: { width: 2, height: 20, marginLeft: -6, backgroundColor: '#A78BFA', borderRadius: 1 },
    beamRow: {
        position: 'absolute', left: 0, right: 0, top: 0, height: ROW_H, borderRadius: 12,
        backgroundImage: 'linear-gradient(90deg, rgba(74,222,128,0) 0%, rgba(74,222,128,0.10) 30%, rgba(74,222,128,0.18) 100%)',
    },
    row: { height: ROW_H, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: 'transparent', gap: 10 },
    rowBest: { borderColor: 'rgba(74,222,128,0.4)', backgroundColor: 'rgba(74,222,128,0.06)' },
    book: { flex: 1, fontFamily: FONT, fontSize: 15, fontWeight: '600', color: FL.ink2 },
    bestTag: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: '#4ADE80' },
    bestTagText: { fontFamily: FONT, fontSize: 11, fontWeight: '800', color: '#0B0B0B', letterSpacing: 0.8, textTransform: 'uppercase' },
    implied: { width: 56, textAlign: 'right', fontFamily: FONT, fontSize: 13, color: FL.muted },
    price: { width: 58, textAlign: 'right', fontFamily: FONT, fontSize: 17, fontWeight: '700', color: FL.ink },
    stats: { marginTop: 18, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.10)', flexDirection: 'row', gap: 12 },
    stat: { flex: 1, minWidth: 0, gap: 4 },
    statV: { fontFamily: FONT, fontSize: 22, fontWeight: '800', color: '#fff', letterSpacing: -0.4 },
    bandNote: { marginTop: 14, maxWidth: 760 },
    section: { marginTop: 112 },
    grid: { marginTop: 36, gap: 16 },
    gridWide: { flexDirection: 'row' },
    step: { ...glass({ radius: 20 }), padding: 24, gap: 10 },
    stepNum: { fontFamily: FONT, fontSize: 44, fontWeight: '800', color: '#C4A1FF', letterSpacing: -1.5, lineHeight: 46 },
    cardTitle: { fontFamily: FONT, fontSize: 19, fontWeight: '700', color: '#fff', letterSpacing: -0.2, marginBottom: 2 },
    feature: { ...glass({ radius: 20 }), padding: 22, flexDirection: 'row', gap: 16 },
    featureWide: { flexBasis: '48%', flexGrow: 1 },
    featureIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(124,58,237,0.18)', borderWidth: 1, borderColor: 'rgba(196,161,255,0.25)' },
    closer: { ...glass({ strong: true, radius: 28 }), padding: 28, backgroundImage: 'radial-gradient(ellipse 70% 90% at 100% 0%, rgba(124,58,237,0.22) 0%, transparent 70%)' },
    closerWide: { flexDirection: 'row', alignItems: 'center', padding: 56 },
    promise: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    footer: { marginTop: 96, paddingTop: 28, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)' },
    rg: { flexDirection: 'row', alignItems: 'flex-start', gap: 14, maxWidth: 820, alignSelf: 'center', marginBottom: 8 },
    badge21: { width: 40, height: 40, borderRadius: 20, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.5)', alignItems: 'center', justifyContent: 'center' },
    badge21Text: { fontFamily: FONT, fontSize: 13, fontWeight: '800', color: '#fff' },
});
